import JSZip from 'jszip';

export interface SkillMarkdownFrontmatter {
  name?: string;
  description?: string;
  summary?: string;
  overview?: string;
  category?: string;
}

export interface ParsedSkillMarkdown {
  frontmatter: SkillMarkdownFrontmatter;
  content: string;
}

export interface SkillPackageEntry {
  path: string;
  name: string;
  extension: string;
  role: string;
  isPrimarySkillReference: boolean;
}

export interface ParsedSkillBundle {
  skillFilePath: string;
  frontmatter: SkillMarkdownFrontmatter;
  content: string;
  entries: SkillPackageEntry[];
}

const ZIP_MIME_TYPE = 'application/zip';
const skillMarkdownRegex = /^---[ \t]*\r?\n([\s\S]*?)\r?\n---[ \t]*\r?\n?([\s\S]*)$/;
const supportedFrontmatterKeys = ['name', 'description', 'summary', 'overview', 'category'] as const;

type SupportedFrontmatterKey = (typeof supportedFrontmatterKeys)[number];

function getFileName(path: string) {
  return path.split('/').filter(Boolean).pop() ?? path;
}

function getFileExtension(path: string) {
  const fileName = getFileName(path);
  const extension = fileName.includes('.') ? fileName.split('.').pop() : undefined;
  return extension?.toLowerCase() ?? '';
}

function formatFileExtension(path: string) {
  return getFileExtension(path).toUpperCase() || 'FILE';
}

function getPackageEntryRole(path: string, skillFilePath: string) {
  if (path === skillFilePath) {
    return 'Primary skill reference';
  }

  const extension = getFileExtension(path);

  if (extension === 'py') {
    return 'Script';
  }

  if (['js', 'jsx', 'ts', 'tsx', 'mjs', 'cjs'].includes(extension)) {
    return 'Helper script';
  }

  if (['md', 'mdx', 'txt'].includes(extension)) {
    return 'Reference';
  }

  if (['ppt', 'pptx', 'doc', 'docx', 'xls', 'xlsx', 'pdf', 'csv'].includes(extension)) {
    return 'Template';
  }

  if (['json', 'yaml', 'yml'].includes(extension)) {
    return 'Configuration';
  }

  return 'Asset';
}

function normalizeFrontmatterKey(key: string): SupportedFrontmatterKey | null {
  const normalizedKey = key.trim().toLowerCase();
  return supportedFrontmatterKeys.includes(normalizedKey as SupportedFrontmatterKey)
    ? (normalizedKey as SupportedFrontmatterKey)
    : null;
}

function parseFrontmatterValue(value: string): string {
  const trimmedValue = value.trim();

  if (!trimmedValue) {
    return '';
  }

  if (
    (trimmedValue.startsWith('"') && trimmedValue.endsWith('"')) ||
    (trimmedValue.startsWith("'") && trimmedValue.endsWith("'"))
  ) {
    try {
      return trimmedValue.startsWith('"') ? String(JSON.parse(trimmedValue)) : trimmedValue.slice(1, -1);
    } catch {
      return trimmedValue.slice(1, -1);
    }
  }

  return trimmedValue;
}

function getLineIndent(line: string) {
  return line.match(/^\s*/)?.[0].length ?? 0;
}

function stripCommonIndent(lines: string[]) {
  const nonEmptyIndents = lines.filter((line) => line.trim()).map(getLineIndent);
  const commonIndent = nonEmptyIndents.length > 0 ? Math.min(...nonEmptyIndents) : 0;

  return lines.map((line) => (line.trim() ? line.slice(commonIndent) : ''));
}

function foldBlockScalarLines(lines: string[]) {
  const foldedLines: string[] = [];
  let currentParagraph: string[] = [];

  for (const line of lines) {
    const trimmedLine = line.trim();

    if (!trimmedLine) {
      if (currentParagraph.length > 0) {
        foldedLines.push(currentParagraph.join(' '));
        currentParagraph = [];
      }
      foldedLines.push('');
      continue;
    }

    currentParagraph.push(trimmedLine);
  }

  if (currentParagraph.length > 0) {
    foldedLines.push(currentParagraph.join(' '));
  }

  return foldedLines.join('\n').trim();
}

function parseBlockScalarValue(lines: string[], style: string) {
  const normalizedLines = stripCommonIndent(lines);

  if (style === '>') {
    return foldBlockScalarLines(normalizedLines);
  }

  return normalizedLines.join('\n').trim();
}

function parseSkillFrontmatter(rawFrontmatter: string): SkillMarkdownFrontmatter {
  const frontmatter: SkillMarkdownFrontmatter = {};
  const lines = rawFrontmatter.split(/\r?\n/);

  for (let index = 0; index < lines.length; index += 1) {
    const line = lines[index];
    const blockScalarMatch = line.match(/^(\s*)([A-Za-z][\w-]*)\s*:\s*([>|])[-+]?([1-9])?\s*$/);
    const blockScalarKey = blockScalarMatch ? normalizeFrontmatterKey(blockScalarMatch[2]) : null;

    if (blockScalarMatch && blockScalarKey) {
      const parentIndent = blockScalarMatch[1].length;
      const blockLines: string[] = [];
      let nextIndex = index + 1;

      while (nextIndex < lines.length) {
        const nextLine = lines[nextIndex];

        if (nextLine.trim() && getLineIndent(nextLine) <= parentIndent) {
          break;
        }

        blockLines.push(nextLine);
        nextIndex += 1;
      }

      frontmatter[blockScalarKey] = parseBlockScalarValue(blockLines, blockScalarMatch[3]);
      index = nextIndex - 1;
      continue;
    }

    const match = line.match(/^\s*([A-Za-z][\w-]*)\s*:\s*(.*?)\s*$/);
    const key = match ? normalizeFrontmatterKey(match[1]) : null;

    if (!key) {
      continue;
    }

    frontmatter[key] = parseFrontmatterValue(match?.[2] ?? '');
  }

  return frontmatter;
}

export function buildSkillPackageEntries(filePaths: string[], skillFilePath: string) {
  return filePaths
    .map((path) => ({
      path,
      name: getFileName(path),
      extension: formatFileExtension(path),
      role: getPackageEntryRole(path, skillFilePath),
      isPrimarySkillReference: path === skillFilePath,
    }))
    .sort((left, right) => {
      if (left.isPrimarySkillReference) {
        return -1;
      }

      if (right.isPrimarySkillReference) {
        return 1;
      }

      return left.path.localeCompare(right.path);
    });
}

function formatFrontmatterValue(value: string) {
  return JSON.stringify(value);
}

export function readBlobAsArrayBuffer(blob: Blob): Promise<ArrayBuffer> {
  if (typeof blob.arrayBuffer === 'function') {
    return blob.arrayBuffer();
  }

  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      if (reader.result instanceof ArrayBuffer) {
        resolve(reader.result);
        return;
      }

      reject(new Error('Unable to read the selected custom skill bundle.'));
    };
    reader.onerror = () => reject(reader.error ?? new Error('Unable to read the selected custom skill bundle.'));
    reader.readAsArrayBuffer(blob);
  });
}

export function parseSkillMarkdown(raw: string): ParsedSkillMarkdown {
  const normalizedRaw = raw.replace(/^\uFEFF/, '');
  const match = normalizedRaw.match(skillMarkdownRegex);

  if (!match) {
    return {
      frontmatter: {},
      content: raw,
    };
  }

  return {
    frontmatter: parseSkillFrontmatter(match[1] ?? ''),
    content: (match[2] ?? '').replace(/^\r?\n/, ''),
  };
}

export function parseSkillMarkdownBody(raw: string) {
  return parseSkillMarkdown(raw).content;
}

export function buildSkillMarkdown({
  frontmatter,
  content,
}: {
  frontmatter: Required<Pick<SkillMarkdownFrontmatter, 'name' | 'description'>> &
    Pick<SkillMarkdownFrontmatter, 'summary' | 'overview' | 'category'>;
  content: string;
}) {
  const category = frontmatter.category?.trim();
  const summary = frontmatter.summary?.trim();
  const overview = frontmatter.overview?.trim();
  const frontmatterLines = [
    `name: ${formatFrontmatterValue(frontmatter.name.trim())}`,
    `description: ${formatFrontmatterValue(frontmatter.description.trim())}`,
    ...(summary ? [`summary: ${formatFrontmatterValue(summary)}`] : []),
    ...(overview ? [`overview: ${formatFrontmatterValue(overview)}`] : []),
    ...(category ? [`category: ${formatFrontmatterValue(category)}`] : []),
  ];

  return `---\n${frontmatterLines.join('\n')}\n---\n\n${content}`;
}

export async function extractSkillBundleFromZipBuffer(buffer: ArrayBuffer): Promise<ParsedSkillBundle> {
  const zip = await JSZip.loadAsync(buffer);
  const fileEntries = Object.values(zip.files).filter((file) => !file.dir);
  const skillManifestEntries = fileEntries.filter((file) => file.name.split('/').pop()?.toLowerCase() === 'skill.md');

  if (skillManifestEntries.length !== 1) {
    throw new Error('Exactly one SKILL.md file must be present in the custom skill bundle.');
  }

  const skillMarkdown = await skillManifestEntries[0].async('string');
  const parsedSkillMarkdown = parseSkillMarkdown(skillMarkdown);
  const skillFilePath = skillManifestEntries[0].name;

  return {
    skillFilePath,
    frontmatter: parsedSkillMarkdown.frontmatter,
    content: parsedSkillMarkdown.content,
    entries: buildSkillPackageEntries(
      fileEntries.map((file) => file.name),
      skillFilePath,
    ),
  };
}

export async function extractSkillContentFromZipBuffer(buffer: ArrayBuffer) {
  const bundle = await extractSkillBundleFromZipBuffer(buffer);
  return bundle.content;
}

export async function buildSkillZipFile({
  sourceFile,
  skillFilePath,
  frontmatter,
  content,
}: {
  sourceFile: File;
  skillFilePath: string;
  frontmatter: Required<Pick<SkillMarkdownFrontmatter, 'name' | 'description'>> &
    Pick<SkillMarkdownFrontmatter, 'summary' | 'overview' | 'category'>;
  content: string;
}) {
  const zip = await JSZip.loadAsync(await readBlobAsArrayBuffer(sourceFile));

  if (!zip.file(skillFilePath)) {
    throw new Error('Unable to find SKILL.md in the selected custom skill bundle.');
  }

  zip.file(skillFilePath, buildSkillMarkdown({ frontmatter, content }));
  const blob = await zip.generateAsync({ type: 'blob' });
  return new File([blob], sourceFile.name, { type: sourceFile.type || ZIP_MIME_TYPE });
}

export async function fetchSkillContentFromZipUrl(url: string) {
  const bundle = await fetchSkillBundleFromZipUrl(url);
  return bundle.content;
}

export async function fetchSkillBundleFromZipUrl(url: string) {
  const response = await fetch(url);

  if (!response.ok) {
    throw new Error('Unable to download the custom skill bundle.');
  }

  return extractSkillBundleFromZipBuffer(await response.arrayBuffer());
}
